import { Box, Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { setFiltersToLocalStorage } from "../../../common/components/functional/FilterSearchField/helpers";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { Nullable } from "../../../common/types/nullable";
import useIsLoading from "../../../hooks/useIsLoading";
import useMessage from "../../../hooks/useMessage";
import DashboardHeaderRow from "../DashboardHeaderRow";
import { fetchHrTasklist, hrTaskAcknowledge } from "../slice/dashboardActions";
import { selectAllHrTaskResponses } from "../slice/dashboardSlice";
import HrTaskContentRows from "./HrTaskContentRows";
import HrTasklist from "./HrTasklist";
import { HR_TASK_LIST_FILTERS_STORAGE_KEY } from "./constants";
import HrTaskListFilters from "./filters/HrTaskListFilters";
import {
  loadHrTaskListFilters,
  retainVisibleCategories,
  toHrTaskListQuery,
  visibleHrTaskFilterCategories,
} from "./filters/common";
import { toHrTask } from "./hrTaskAdapter";

const HrTasklistContainer = () => {
  const [selectedHrTaskId, setSelectedHrTaskId] =
    useState<Nullable<string>>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<SearchFilter[]>(loadHrTaskListFilters);
  const localized = useAppSelector((state) => state.localized);
  const user = useAppSelector((state) => state.user.user);
  const responses = useAppSelector(selectAllHrTaskResponses);
  const { t, i18n } = useTranslation("dashboard");
  const language = i18n.language;
  const tasks = useMemo(
    () =>
      responses.map((response) =>
        toHrTask(response, { language, t, localized }),
      ),
    [responses, language, t, localized],
  );
  const selectedHrTask = tasks.find((task) => task.id === selectedHrTaskId);
  const { sendMessage } = useMessage();
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(["dashboard.hrTasklist"]);
  const visibleCategories = useMemo(
    () => visibleHrTaskFilterCategories(user),
    [user],
  );
  const categoryLabel = useCallback(
    (categoryId: string) => t(`hrTasklist.filters.categories.${categoryId}`),
    [t],
  );

  // Role-disallowed categories are removed before the chips and the list query render.
  const visibleFilters = useMemo(
    () =>
      user
        ? retainVisibleCategories(
            filters,
            visibleCategories.map((category) => category.id),
            categoryLabel,
          )
        : filters,
    [categoryLabel, filters, user, visibleCategories],
  );
  const hrTaskListQuery = useMemo(
    () =>
      toHrTaskListQuery(
        visibleFilters,
        searchText,
        format(new Date(), isoFormat),
        visibleCategories,
      ),
    [searchText, visibleCategories, visibleFilters],
  );

  const updateFilters = useCallback((nextFilters: SearchFilter[]) => {
    setFilters(nextFilters);
    setFiltersToLocalStorage(nextFilters, HR_TASK_LIST_FILTERS_STORAGE_KEY);
  }, []);

  const handleFilterMenuOpenClose = useCallback(() => {
    setIsFilterMenuOpen((isOpen) => !isOpen);
  }, []);

  useEffect(() => {
    if (!user || visibleFilters === filters) return;
    updateFilters(visibleFilters);
  }, [filters, updateFilters, user, visibleFilters]);

  useEffect(() => {
    if (!user) return;
    dispatch(fetchHrTasklist(hrTaskListQuery));
  }, [dispatch, hrTaskListQuery, user]);

  useEffect(() => {
    responses.forEach((response) => {
      if ((response.presentation != null) !== (response.actions != null)) {
        console.warn(
          "HR task has incomplete presentation/actions",
          response.id,
        );
      }
    });
  }, [responses]);

  const onDialogClose = () => setSelectedHrTaskId(null);

  const onDialogConfirm = async () => {
    if (!selectedHrTask) return;

    const action = await dispatch(
      hrTaskAcknowledge({
        hrTaskId: selectedHrTask.id,
        stamp: Date.now(),
      }),
    );
    if (isFulfilled(action)) {
      sendMessage(t("hrTasklist.taskMarkedAsDone"), "success");
      onDialogClose();
    }
  };

  return (
    <Grid
      item
      xs={3.1}
      sx={{
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <DashboardHeaderRow title={t("hrTasklist.title")} />
        <FilterSearchField
          width="100%"
          fitContainer
          flat
          isOpen={isFilterMenuOpen}
          toggleMenu={handleFilterMenuOpenClose}
          label={t("hrTasklist.filters.searchPlaceholder")}
          value={searchText}
          onChange={setSearchText}
          searchHistory={false}
          hideSearchHistory
          hideReturnDefaultFilters
          hideEmptyFilterRow
          collapsibleFilters
          translation="dashboard"
          filters={visibleFilters}
          updateFilters={updateFilters}
          debounceSearchMs={400}
        >
          <HrTaskListFilters
            filters={visibleFilters}
            updateFilters={updateFilters}
            handleMenuToggle={handleFilterMenuOpenClose}
            categories={visibleCategories}
          />
        </FilterSearchField>
      </Box>
      <HrTasklist
        isLoading={isLoading}
        tasks={tasks}
        handleSelectHrTask={setSelectedHrTaskId}
        hrTaskListQuery={hrTaskListQuery}
      />
      <ConfirmationDialog
        isOpen={!!selectedHrTask}
        title={selectedHrTask?.actions.confirmation.title ?? ""}
        contentText={
          selectedHrTask ? (
            <HrTaskContentRows
              rows={selectedHrTask.actions.confirmation.rows}
            />
          ) : (
            ""
          )
        }
        confirmText={t("hrTasklist.confirmButtonText")}
        cancelText={t("common:cancel")}
        onConfirm={onDialogConfirm}
        onCancel={onDialogClose}
        isLoading={isLoading}
      />
    </Grid>
  );
};

export default HrTasklistContainer;
